import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { getGoalBySlug } from 'Shared/selectors/Goals.selectors';

import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import RadioInput from 'Shared/components/RadioInput/RadioInput';
import GoalStatus from 'Shared/components/Goals/GoalStatus/GoalStatus';

import './CompleteGoalModal.scss';

type Props = {
  onToggle: () => void;
  onSubmit: Function;
  goalSlug: string;
  goalResolutionNote: string;
};

export default function CompleteGoalModal({ goalSlug, goalResolutionNote, onToggle, onSubmit }: Props) {
  const [formValues, setFormValues] = useState({ resolutionNote: goalResolutionNote || '', closeActionItems: true });
  const goal = useSelector(state => getGoalBySlug(state, { slug: goalSlug }))?.toJS();

  const handleResolutionNoteChange = event => setFormValues({ ...formValues, resolutionNote: event.target.value });

  const handleCompleteActionItemsClick = () => setFormValues({ ...formValues, closeActionItems: true });

  const handleKeepActionItemsClick = () => setFormValues({ ...formValues, closeActionItems: false });

  const handleCompleteClick = () => {
    onSubmit({ goalSlug, ...formValues });
    onToggle();
  };

  const baseClassName = 'CompleteGoalModal';

  return (
    <WpModal size="sm" isOpen onToggle={onToggle}>
      <WpModalHeader>Complete Goal</WpModalHeader>
      <WpModalBody>
        <div className={`${baseClassName}`}>
          <div className={`${baseClassName}__title`}>Update status:</div>
          <div className={`${baseClassName}__status`}>
            <GoalStatus goal={goal} editable />
          </div>
          <div className={`${baseClassName}__title`}>Record goal results and other notable outcomes.</div>
          <textarea
            placeholder="How’d it go? Anything noteworthy?"
            className={`${baseClassName}__textarea`}
            value={formValues.resolutionNote}
            onChange={handleResolutionNoteChange}
          />
          {goal.numIncompleteActionItems > 0 && (
            <>
              <div className={`${baseClassName}__title`}>Close Action Items?</div>
              <div className={`${baseClassName}__description`}>
                There are <b>{`${goal.numIncompleteActionItems}`} open action items</b> on this goal. Would you like to
                mark them complete or keep open?
              </div>
              <div>
                <RadioInput
                  checked={formValues.closeActionItems}
                  label="Complete action items"
                  onClick={handleCompleteActionItemsClick}
                />
                <RadioInput
                  checked={!formValues.closeActionItems}
                  label="Keep action items open"
                  onClick={handleKeepActionItemsClick}
                />
              </div>
            </>
          )}
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle}>
            Cancel
          </WpButton>
          <WpButton qaTest="complete-goal-submit-button" onClick={handleCompleteClick}>
            Complete
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}
